import { Scale, Users, Award, Target, Shield, Heart, Gavel, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* ================= HERO SECTION ================= */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4wNSIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-24 relative">
          <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Scale className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-yellow-400" />
            <span className="text-yellow-400 text-xs sm:text-sm font-semibold tracking-widest uppercase">About Blackthorn Chambers</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
            Strategic Legal Solutions<br />
            <span className="text-yellow-400">Built on Excellence</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-3xl leading-relaxed">
            A full service law firm delivering results driven legal representation across complex regulatory, 
            criminal, and civil matters with unwavering professionalism and discretion.
          </p>
        </div>
      </section>

      {/* ================= WHO WE ARE ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <div className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4 border border-slate-200">
              Who We Are
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 sm:mb-6">
              Excellence, Discretion & Deep Legal Insight
            </h2>
            <div className="space-y-3 sm:space-y-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              <p>
                Blackthorn Chambers is a full service law firm committed to delivering strategic, 
                results driven legal solutions with the highest standards of professionalism and integrity.
              </p>
              <p>
                We represent individuals, businesses, and institutions across a wide spectrum of legal matters, 
                with specialized expertise in high stakes regulatory and criminal proceedings.
              </p>
              <p className="font-semibold text-slate-900">
                Our strength lies in our people, driven by some of the finest legal minds in the country, 
                combining courtroom excellence with meticulous preparation and practical legal strategy.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-slate-200/50 rounded-3xl blur-2xl"></div>
            <div className="relative bg-white rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 border border-slate-200">
              <div className="grid grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <Link
                  to="/practice-areas"
                  className="text-center p-3 sm:p-4 md:p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">ED/PMLA</div>
                  <div className="text-xs sm:text-sm text-slate-600">Specialized Practice</div>
                </Link>
                <Link
                  to="/practice-areas"
                  className="text-center p-3 sm:p-4 md:p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">GST & IT</div>
                  <div className="text-xs sm:text-sm text-slate-600">Tax Litigation</div>
                </Link>
                <Link
                  to="/practice-areas"
                  className="text-center p-3 sm:p-4 md:p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">White Collar</div>
                  <div className="text-xs sm:text-sm text-slate-600">Crime Defence</div>
                </Link>
                <Link
                  to="/practice-areas"
                  className="text-center p-3 sm:p-4 md:p-6 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all duration-300 hover:scale-105 cursor-pointer"
                >
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-1 sm:mb-2">Cyber Law</div>
                  <div className="text-xs sm:text-sm text-slate-600">Digital Crimes</div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PRACTICE AREAS OVERVIEW ================= */}
      <section className="bg-slate-50 py-12 sm:py-16 md:py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">Our Expertise</h2>
            <p className="text-slate-600 text-base sm:text-lg max-w-3xl mx-auto">
              We specialize in complex and high stakes cases across regulatory, criminal, and civil matters.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                title: 'Enforcement Directorate & PMLA',
                desc: 'Extensive experience navigating ED proceedings, PMLA investigations, prosecutions, and appellate proceedings with robust representation at every stage.'
              },
              {
                title: 'GST & Income Tax',
                desc: 'Strategic advisory and litigation services for GST and Income Tax matters, handling assessments, appeals, and compliance with precision.'
              },
              {
                title: 'Criminal Litigation',
                desc: 'Comprehensive criminal defence across all stages from investigation to trial with meticulous preparation and aggressive advocacy.'
              },
              {
                title: 'White Collar Crimes',
                desc: 'Specialized representation in financial fraud, embezzlement, corruption, and corporate criminal matters requiring discretion and expertise.'
              },
              {
                title: 'Cyber Crimes',
                desc: 'Defence in digital and cyber related offences including data breaches, online fraud, and technology related criminal proceedings.'
              },
              {
                title: 'Civil Litigation & Advisory',
                desc: 'Strong practice in property disputes, commercial disputes, contractual matters, and strategic civil advisory tailored to client needs.'
              }
            ].map((area, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4">{area.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{area.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= MISSION & APPROACH ================= */}
      <section className="bg-white py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Our Approach */}
            <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-900 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Our Approach</h3>
              </div>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-4">
                We do not believe in one size fits all solutions. Every case is approached with careful analysis, 
                strategic foresight, and relentless advocacy.
              </p>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                Our clients are represented with the highest level of expertise, diligence, and confidence ensuring 
                their interests are protected at every stage.
              </p>
            </div>

            {/* What Sets Us Apart */}
            <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-lg hover:shadow-xl transition-all duration-300">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-900 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">What Sets Us Apart</h3>
              </div>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed mb-4">
                Built on a foundation of excellence, discretion, and deep legal insight, we combine courtroom 
                brilliance with meticulous case preparation.
              </p>
              <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                We approach every matter with precision, confidentiality, and an unwavering commitment to achieving 
                favorable outcomes for our clients.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CORE VALUES ================= */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 md:py-20">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-block px-4 py-2 bg-slate-100 text-slate-700 rounded-full text-xs sm:text-sm font-semibold mb-3 sm:mb-4 border border-slate-200">
            Our Foundation
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-3 sm:mb-4">Core Principles</h2>
          <p className="text-slate-600 text-base sm:text-lg max-w-2xl mx-auto px-4">
            The values that define every case we handle and every client relationship we build.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {[
            { 
              icon: Shield, 
              title: 'Integrity', 
              desc: 'We uphold the highest standards of professionalism and ethical conduct in all our legal practice.'
            },
            { 
              icon: Award, 
              title: 'Excellence', 
              desc: 'Driven by some of the finest legal minds, we combine courtroom skill with strategic legal thinking.'
            },
            { 
              icon: Heart, 
              title: 'Discretion', 
              desc: 'Confidentiality and discretion are paramount. We protect our clients\' interests with complete privacy.'
            },
            { 
              icon: Gavel, 
              title: 'Results Driven', 
              desc: 'Strategic advocacy focused on achieving favorable outcomes through meticulous preparation and execution.'
            }
          ].map((value, idx) => (
            <div key={idx} className="group">
              <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-slate-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 h-full">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-900 rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-yellow-500 transition-colors duration-300">
                  <value.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-slate-900 mb-3 sm:mb-4">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{value.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= CTA SECTION ================= */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12 sm:py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-4 sm:mb-6">
            <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 text-yellow-400" />
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">Facing Complex Legal Challenges?</h2>
          <p className="text-slate-300 text-base sm:text-lg md:text-xl mb-6 sm:mb-8 leading-relaxed px-4">
            Experience strategic legal representation backed by excellence, expertise, and unwavering commitment to your defense.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-bold px-8 sm:px-10 py-3 sm:py-4 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-2xl text-base sm:text-lg"
          >
            Schedule a Consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
